<template>
  <div class="fade-in">
    <div v-if="showEncounter" class="columns">
      <div class="column">
        <div class="content">
          <div v-if="video.session" class="video">
            <meet-publisher
              :session="video.session"
              @error="videoError"
              @publisherConnected="publisherConnected"
            ></meet-publisher>
            <div
              id="subscribers"
              v-for="stream in video.streams"
              :key="stream.streamId"
            >
              <meet-subscriber
                @error="videoError"
                :stream="stream"
                :session="video.session"
              ></meet-subscriber>
            </div>
          </div>
          <div v-if="encounter.attributes.videoUrl" class="video">
            <video-player
              class="video-player-box"
              :options="playerOptions"
              :playsinline="true"
            ></video-player>
          </div>
        </div>
        <div class="mt2">
          <div class="pull-right">
            <button
              v-if="!static"
              type="button"
              class="button is-link"
              @click="toggleTemplateList"
            >
              Templates
            </button>
          </div>
          <h2>Visit Notes</h2>
          <inline-edit
            title
            field="title"
            :canEdit="!static && !isUser"
            :highlight-field="highlight"
            :value="encounter.attributes.title"
            :onSubmit="onInlineSubmit"
          ></inline-edit>
        </div>
        <div class="notes mt2">
          <div class="notes-status pull-right">{{ noteStatus }}</div>
          <quill-editor
            v-if="!static"
            :content="encounter.attributes.notes"
            :options="editorOptions"
            @change="saveNotes($event)"
          ></quill-editor>

          <div
            v-if="static"
            class="static-notes"
            v-html="encounter.attributes.notes"
          ></div>
        </div>

        <div v-if="hasImages()">
          <h2 class="mt3">Images</h2>
          <div class="columns">
            <div
              class="column pointer"
              v-for="image in Object.values(
                encounter.relationships.interviewImages
              )"
              @click="showImageModal(image)"
            >
              <img :src="image.attributes.url" alt width="150" />
              <br />
              {{ image.attributes.imageFileName }}
            </div>
          </div>
        </div>
      </div>

      <div class="column is-4">
        <div class="patient-details mb2 ph1 pv1">
          <h1>
            {{ encounter.relationships.patient.attributes.firstName }}
            {{ encounter.relationships.patient.attributes.lastName }}
          </h1>
          <inline-edit
            title="Preferred Name:"
            field="preferredName"
            :canEdit="!static && !isUser"
            :highlight-field="highlight"
            :inverse="true"
            :value="encounter.relationships.patient.attributes.preferredName"
            :onSubmit="onPatientSubmit"
            :inline="true"
          ></inline-edit>

          <h3 style="display: inline-block">Age:</h3>
          {{ age(encounter.relationships.patient.attributes.birthday) }}
          <inline-edit
            title="DOB:"
            field="birthday"
            :canEdit="!static && !isUser"
            :highlight-field="highlight"
            :inverse="true"
            :value="encounter.relationships.patient.attributes.birthday"
            :onSubmit="onPatientSubmit"
            :inline="true"
          ></inline-edit>
        </div>

        <div class="face-sheet">
          <inline-edit
            title="Reason For Visit"
            field="reasonForVisit"
            :canEdit="!static && !isUser"
            :highlight-field="highlight"
            :value="encounter.attributes.reasonForVisit"
            :onSubmit="onInlineSubmit"
          ></inline-edit>

          <inline-edit-reason
            :canEdit="!static && !isUser"
            :highlight-field="highlight"
            :value="encounter.attributes.reviewOfSystems"
            :onSubmit="onInlineSubmit"
          ></inline-edit-reason>

          <inline-edit
            title="Insurance"
            field="insurance"
            :canEdit="!static && !isUser"
            :highlight-field="highlight"
            :value="encounter.attributes.insurance"
            :onSubmit="onInlineSubmit"
          ></inline-edit>

          <inline-edit
            title="Meds"
            field="meds"
            :canEdit="!static && !isUser"
            :highlight-field="highlight"
            :value="encounter.attributes.meds"
            :onSubmit="onInlineSubmit"
          ></inline-edit>

          <inline-edit
            title="Allergies"
            field="allergies"
            :canEdit="!static && !isUser"
            :highlight-field="highlight"
            :value="encounter.attributes.allergies"
            :onSubmit="onInlineSubmit"
          ></inline-edit>

          <inline-edit
            title="Medical History"
            field="medicalHistory"
            :canEdit="!static && !isUser"
            :highlight-field="highlight"
            :value="encounter.attributes.medicalHistory"
            :onSubmit="onInlineSubmit"
          ></inline-edit>
        </div>
      </div>
    </div>

    <div class="encounter-footer has-text-right mt2">
      <button
        v-if="!static"
        type="button"
        :class="[
          'button',
          'is-primary',
          'is-large',
          { 'is-loading': isSaving }
        ]"
        @click="stopSession"
      >
        Save
      </button>
      <button
        v-if="!static"
        type="button"
        :class="[
          'button',
          'is-primary',
          'is-large',
          { 'is-loading': isSigning }
        ]"
        @click="sign"
      >
        Save & Sign
      </button>
      <router-link
        v-if="static"
        :to="{ name: 'encounter-list' }"
        class="button is-primary is-large"
        >Done</router-link
      >

      <div
        :class="['dropdown', 'is-up', { 'is-active': showExtraMenu }]"
        @click="toggleExtraMenu"
      >
        <div class="dropdown-trigger">
          <button class="button is-large is-link">
            <span>...</span>
          </button>
        </div>
        <div class="dropdown-menu" role="menu">
          <div class="dropdown-content">
            <div class="dropdown-item">
              <button
                type="button"
                class="button is-primary is-link"
                @click="showCareTemplateSaveModal"
              >
                Save as Template
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <vue-toastr ref="toastr"></vue-toastr>

    <div v-if="showEncounter">
      <image-modal
        :show="showModal"
        :image="modalImage"
        :hide="hideImageModal"
      />
      <care-template-save-modal
        :show="showCareTemplateSave"
        :body="encounter.attributes.notes"
        :hide="hideCareTemplateSave"
        :onSubmit="saveTemplate"
        :working="isSavingTemplate"
      />
      <care-templates-modal
        :show="showTemplateList"
        :templates="careTemplates"
        :hide="toggleTemplateList"
        :onSubmit="careTemplateOnSubmit"
      />
    </div>
  </div>
</template>

<script>
/* eslint func-names: ["error", "never"] */

import OT from "@opentok/client";
import moment from "moment";
import _ from "lodash";
import Pusher from "pusher-js";
import { camelize } from "humps";
import { quillEditor } from "vue-quill-editor";
import { videoPlayer } from "vue-video-player";
import EncountersService from "@/services/EncountersService";
import InlineEdit from "./InlineEdit";
import InlineEditReason from "./InlineEditReason";
import "../../../node_modules/quill/dist/quill.snow.css";
import "../../../node_modules/video.js/dist/video-js.css";
import "../../../node_modules/vue-video-player/src/custom-theme.css";
import ImageModal from "../patient/ImageModal";
import CareTemplateService from "../../services/CareTemplateService";
import CareTemplateSaveModal from "./CareTemplateSaveModal";
import CareTemplatesModal from "./CareTemplatesModal";
import PatientsService from "../../services/PatientsService";
import MeetPublisher from "./MeetPublisher";
import MeetSubscriber from "./MeetSubscriber";

export default {
  name: "encounter-meet",

  props: ["id"],

  components: {
    CareTemplatesModal,
    CareTemplateSaveModal,
    InlineEdit,
    InlineEditReason,
    quillEditor,
    videoPlayer,
    ImageModal,
    MeetPublisher,
    MeetSubscriber
  },

  data() {
    return {
      showEncounter: false,
      encounter: { attributes: { status: "" } },
      highlight: false,
      noteStatus: "",
      editorOptions: {
        modules: {
          toolbar: [
            [{ header: [1, 2, false] }],
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }]
          ]
        },
        theme: "snow",
        placeholder: "Your notes"
      },
      video: {
        session: null,
        streams: []
      },
      isSaving: false,
      isSigning: false,
      playerOptions: {
        language: "en",
        playbackRates: [0.7, 1.0, 1.5, 2.0],
        sources: [
          {
            type: "video/mp4",
            src: ""
          }
        ]
      },
      showModal: false,
      modalImage: {},
      showExtraMenu: false,
      isSavingTemplate: false,
      showCareTemplateSave: false,
      showTemplateList: false
    };
  },

  computed: {
    isUser() {
      return this.$store.getters.isUser;
    },
    careTemplates() {
      return this.$store.getters["careTemplate/templates"];
    },
    static() {
      return this.encounter.attributes.status === "completed";
    }
  },

  methods: {
    age(birthday) {
      const now = moment();
      const dob = moment(birthday);
      return parseInt(moment.duration(now.diff(dob)).asYears(), 10);
    },
    onInlineSubmit(field, value) {
      return EncountersService.updateFaceData(
        this.$props.id,
        field,
        value
      ).then(response => {
        this.encounter = response;
        return Promise.resolve();
      });
    },
    onPatientSubmit(field, value) {
      const values = { id: this.encounter.relationships.patient.id };
      values[field] = value;
      return PatientsService.update(values).then(() => this.loadMeeting());
    },
    loadMeeting(highlight = false) {
      return EncountersService.getDetails(this.$props.id)
        .then(response => {
          this.encounter = response;
          this.showEncounter = true;

          if (highlight) {
            this.highlight = highlight;
            setTimeout(() => {
              this.highlight = false;
            }, 1000);
          }

          this.playerOptions.sources[0].src = this.encounter.attributes.videoUrl;
        })
        .catch(() => {
          setTimeout(() => {
            this.$refs.toastr.e(
              "Sorry, you are not allowed to view this meeting"
            );
            this.$router.push({ name: "encounter-list" });
          }, 500);
        });
    },
    saveNotes: _.debounce(function({ html }) {
      this.encounter.attributes.notes = html;
      this.noteStatus = "saving";
      const values = {
        id: this.encounter.id,
        notes: html
      };
      EncountersService.saveNotes(values).then(() => {
        this.noteStatus = "saved";
        setTimeout(() => {
          this.noteStatus = "";
        }, 1000);
      });
    }, 500),

    // Handling all of our errors here by alerting them
    videoError(error) {
      if (error) {
        console.log("ot error", error.message);
      }
    },

    initializeSession(apiKey, sessionId, token) {
      // eslint-disable-next-line
      this.video.session = OT.initSession(apiKey, sessionId);

      this.video.session.connect(token, this.videoError);

      this.video.session.on("streamCreated", event => {
        this.video.streams.push(event.stream);
      });

      this.video.session.on("streamDestroyed", event => {
        const idx = this.video.streams.indexOf(event.stream);
        if (idx > -1) {
          this.video.streams.splice(idx, 1);
        }
      });
    },

    publisherConnected() {
      if (!this.isUser) {
        EncountersService.startArchive(this.$props.id);
      }
    },

    stopSession() {
      this.isSaving = true;

      if (!this.encounter.attributes.videoUrl) {
        EncountersService.stopSession(this.$props.id).then(() => {
          this.isSaving = false;
          this.$refs.toastr.s("The session has been saved and video archived.");
        });
      } else {
        this.isSaving = false;
        this.$refs.toastr.s("The session has been saved.");
      }
    },

    sign() {
      this.isSigning = true;

      EncountersService.signSession(this.$props.id).then(() => {
        this.isSigning = false;
        this.$refs.toastr.s("The session has been saved and signed.");
        this.$router.push({ name: "encounter-list" });
      });
    },
    hasImages() {
      return (
        _.has(this.encounter, "relationships.interviewImages") &&
        this.encounter.relationships.interviewImages
      );
    },
    showImageModal(image) {
      this.showModal = true;
      this.modalImage = image.attributes;
    },
    hideImageModal() {
      this.showModal = false;
    },
    toggleExtraMenu() {
      this.showExtraMenu = !this.showExtraMenu;
    },
    saveTemplate(title) {
      this.isSavingTemplate = true;
      CareTemplateService.create({
        title,
        body: this.encounter.attributes.notes
      }).then(() => {
        this.isSavingTemplate = false;
        this.showCareTemplateSave = false;
        this.$refs.toastr.s("The template has been saved.");
      });
    },
    showCareTemplateSaveModal() {
      this.showCareTemplateSave = true;
    },
    hideCareTemplateSave() {
      this.showCareTemplateSave = false;
    },
    toggleTemplateList() {
      this.showTemplateList = !this.showTemplateList;
    },
    careTemplateOnSubmit(body) {
      this.encounter.attributes.notes = body;
      this.showTemplateList = false;
    }
  },

  mounted() {
    if (this.isUser) {
      // pusher binding to get updates for face data and highlight the changed field
      const pusher = new Pusher(process.env.VUE_APP_PUSHER_KEY, {
        cluster: "us2",
        encrypted: true
      });

      const channel = pusher.subscribe(`meet-${this.$props.id}`);

      channel.bind("face-update", data => {
        if (data.update) {
          this.loadMeeting(camelize(data.field));
        }
      });
    } else {
      CareTemplateService.list();
    }

    this.loadMeeting().then(() => {
      // init video
      if (!this.encounter.attributes.videoUrl) {
        this.initializeSession(
          process.env.VUE_APP_TOKBOX_API_KEY,
          this.encounter.attributes.opentokSessionId,
          this.encounter.attributes.opentokToken
        );
      }
    });
  },

  created() {
    // Add the script to the window object
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = "https://static.opentok.com/v2/js/opentok.min.js";
    document.body.appendChild(script);
  }
};
</script>

<style lang="scss">
.patient-details {
  background: #5f93fb;
  color: #fff;
}

.video {
  position: relative;
  background: #ccc;
  width: 100%;
  height: 500px;

  .video-js {
    width: 100%;
    height: 500px;
  }
}

.notes {
  background: #fff;
}

.notes-status {
  margin: 0.6rem 2rem 0 0;
}

.ql-editor {
  height: 400px;
}

.static-notes {
  padding: 10px;
}

.is-up .dropdown-menu {
  top: -100%;
  right: 0;
  left: unset;
}
</style>
